import './licenses.professional-caregiver.screen.scss';
import {Box, Button, Card, Typography} from '@mui/material';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {TextChart} from '../../../../../../components/charts/text-chart/text-chart';
import {ContentPage} from '../../../../../../components/content-page/content-page';
import {translations} from '../../../../../../i18n/translations';
import {StatisticsRepository} from '../../../../../../repositories/statistics';
import {EntitiesRepository} from '../../../../../../repositories/entities';
import RefreshIcon from '@mui/icons-material/Refresh';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {ScreenPaths} from '../../../../../../enums';
import {useAuth} from '../../../../../../contexts/auth/context';

export default function LicensesProfessionalCaregiverOfficeScreen() {
  let {entityId}: {entityId?: string} = useParams();
  const {state: authState} = useAuth();
  const {t} = useTranslation();
  const [numberActiveSeniors, setNumberActiveSeniors] = useState<number | undefined>();
  const [loadingNumberActiveSeniors, setLoadingNumberActiveSeniors] = useState<boolean>(true);
  const [numberMonthlyActiveLicenses, setNumberMonthlyActiveLicenses] = useState<
    number | undefined
  >();
  const [loadingNumberMonthlyActiveLicenses, setLoadingNumberMonthlyActiveLicenses] =
    useState<boolean>(true);

  // Get licenses data for the entity
  useEffect(() => {
    // Get data
    getLicensesData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  // Get licenses data
  const getLicensesData = () => {
    getNumberMonthlyActiveLicenses();

    getNumberActiveSeniors();
  };

  // Get the number of monthly active licenses
  const getNumberMonthlyActiveLicenses = async () => {
    try {
      // Start loading
      setLoadingNumberMonthlyActiveLicenses(true);

      // Check id
      if (!entityId) {
        // Error
        throw new Error('Empty entity id.');
      }

      // Make request to the backend
      const result = await EntitiesRepository.instance.getEntityLicenses(entityId);

      // Set value
      // Note: The backend will return -1 when the value is not defined
      setNumberMonthlyActiveLicenses(
        result.currentLicensesNumber !== -1 ? result.currentLicensesNumber : undefined
      );
    } catch (error) {
      // Log
      console.debug(
        `🔴 Could not get the number of monthly active licenses for entity ${entityId}.`,
        error
      );

      // Set value
      setNumberMonthlyActiveLicenses(undefined);
    } finally {
      // Stop loading
      setLoadingNumberMonthlyActiveLicenses(false);
    }
  };

  // Get the number of active seniors
  const getNumberActiveSeniors = async () => {
    try {
      // Start loading
      setLoadingNumberActiveSeniors(true);

      // Check id
      if (!entityId) {
        // Error
        throw new Error('Empty entity id.');
      }

      // Make request to the backend
      const result = await StatisticsRepository.instance.getActiveSeniorsCountInEntity(entityId);

      // Set value
      setNumberActiveSeniors(result.activeSeniorsCount);
    } catch (error) {
      // Log
      console.debug(`🔴 Could not get the number of active seniors for entity ${entityId}.`, error);

      // Set value
      setNumberActiveSeniors(undefined);
    } finally {
      // Stop loading
      setLoadingNumberActiveSeniors(false);
    }
  };

  return (
    <Box className="settings-menu-wrapper">
      <GenericHeader
        titleCrumbs={[
          {
            label: t(
              translations.office.professional_caregiver.settings.generic_header.breadcrumbs
                .settings
            ),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Settings}`,
          },
          {
            label: t(
              translations.office.professional_caregiver.settings.generic_header.breadcrumbs
                .licenses
            ),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Settings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Licenses}`,
          },
        ]}
        isLoading={false}
        leftColumn={
          <Typography className="settings-menu-header-title" noWrap>
            {t(
              translations.office.professional_caregiver.settings.generic_header.subtitle.licenses
            )}
          </Typography>
        }
      />
      <Box>
        <Card className="settings-menu-card" elevation={0}>
          <ContentPage>
            <Box className="licenses-professional-caregiver-container">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={getLicensesData}
                disabled={loadingNumberActiveSeniors || loadingNumberMonthlyActiveLicenses}
              >
                {t(translations.licenses.professional_caregiver.root.refresh_button)}
              </Button>
              <Box className="licenses-professional-caregiver-text-charts-container">
                <TextChart
                  value={
                    numberMonthlyActiveLicenses !== undefined &&
                    numberMonthlyActiveLicenses !== null
                      ? numberMonthlyActiveLicenses
                      : t(
                          translations.licenses.professional_caregiver.root
                            .number_of_monthly_active_licenses_not_defined
                        )
                  }
                  title={t(
                    translations.licenses.professional_caregiver.root
                      .number_of_monthly_active_licenses_title
                  )}
                  loading={loadingNumberMonthlyActiveLicenses}
                  handleRefresh={getNumberMonthlyActiveLicenses}
                />
                <TextChart
                  value={numberActiveSeniors}
                  title={t(
                    translations.licenses.professional_caregiver.root.number_of_active_seniors_title
                  )}
                  loading={loadingNumberActiveSeniors}
                  handleRefresh={getNumberActiveSeniors}
                />
              </Box>
            </Box>
          </ContentPage>
        </Card>
      </Box>
    </Box>
  );
}
